import type {
  KbArticle,
  WellTags,
  Dashboard,
  DocumentPage,
  DocumentRow,
  Folder,
  AuditEvent,
  AuditPage,
  Branding,
  LoginResponse,
  Upload,
  User,
  Well,
} from '../types';

/**
 * Front-end only stand-in for the Go API, enabled with VITE_MOCK_API=true.
 *
 * It lets `npm run dev` work on its own — no backend process, no database —
 * which is handy for UI work. Everything here mirrors the shapes the real API
 * returns, so switching the flag off changes nothing in the components.
 */

const DEMO_EMAIL = 'test@test.com';
const DEMO_PASSWORD = 'Test123$';

const DEMO_USER: User = {
  id: 'u-1001',
  email: DEMO_EMAIL,
  name: 'Ignasius Purnomo',
  role: 'Drilling Supervisor',
};

const WELLS: Well[] = [
  { id: 344, name: 'Tapis Deep-1RDR1', field: 'Tapis', rig: 'Sapura Esperanza' },
  { id: 345, name: 'Bekok D-27', field: 'Bekok', rig: 'Hakuryu-11' },
  { id: 346, name: 'Seligi F-14', field: 'Seligi', rig: 'Naga 8' },
];

const MOCK_TOKEN = 'mock.token.not-a-real-jwt';

// A second demo account, so the superadmin-only pages can be opened without a
// backend. Same password as the first.
const ADMIN_EMAIL = 'admin@test.com';
const ADMIN_TOKEN = 'mock.token.superadmin';
const ADMIN_USER: User = {
  id: 'u-1000',
  email: ADMIN_EMAIL,
  name: 'Portal Admin',
  role: 'superadmin',
};

function pad(n: number) {
  return String(n).padStart(2, '0');
}

function stamp(d: Date) {
  return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(
    d.getMinutes(),
  )}:${pad(d.getSeconds())}`;
}

function compact(d: Date) {
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
}

function dashboardFor(well: Well): Dashboard {
  const now = new Date();
  const ts = stamp(now);

  return {
    well,
    currentActivity: 'Tripping Out',
    tiles: [
      { label: 'Hole Depth', value: '2673.37', unit: 'm', tone: 'terminal' },
      { label: 'Bit Depth', value: '0.00', unit: 'm', tone: 'terminal' },
      { label: 'Rig Time', value: ts, tone: 'amber' },
    ],
    rigStates: [
      { label: 'Pumping', active: true },
      { label: 'Rotating', active: true },
      { label: 'On Bottom', active: false },
      { label: 'In Slips', active: false },
    ],
    lastData: ts,
    documents: [
      {
        title: `${well.name.toUpperCase()}_DGOS_85`,
        file: `${well.name}_DGOS_85_${compact(now)}.pdf`,
        page: 1,
        pageCount: 1,
        kind: 'geology',
      },
      {
        title: `${well.name}_Progress Chart`,
        file: `${well.name}_Progress_Chart_${compact(now)}.pdf`,
        page: 1,
        pageCount: 1,
        kind: 'progress',
      },
    ],
    kpis: [
      { label: 'Well Operations Breakdown', value: 5.6, max: 10, unit: '%' },
      { label: 'Well Operations Breakdown', value: 113.5, max: 150, unit: '%' },
      { label: 'Well Cost Utilisation', value: 83.3, max: 100, unit: '%' },
    ],
    updatedAt: now.toISOString(),
  };
}

export interface MockResult {
  status: number;
  body: unknown;
}


/**
 * The folder tree the API seeds for well 344, in the shape the API returns it.
 * Two kinds of node: ordinary folders, and smart folders whose contents are a
 * saved query rather than rows somebody filed.
 */
function folderTree(wellId: number): Folder[] {
  let id = 0;

  const make = (
    name: string,
    opts: { count?: number; smart?: boolean; children?: Folder[] } = {},
  ): Folder => {
    id += 1;
    return {
      id,
      wellId,
      parentId: null,
      name,
      position: id * 10,
      kind: opts.smart ? 'smart' : 'folder',
      documentCount: opts.count ?? 0,
      children: opts.children,
    };
  };

  // Only well 344 has been given a template; the others have no tree at all,
  // which is exactly what the API does.
  if (wellId !== 344) return [];

  return [
    make('Executive Summary'),
    make('Quick Look Daily Reports', { count: 533, smart: true }),
    make('Uploaded Last 24 Hours', { count: 20, smart: true }),
    make('Uploaded Last 48 Hours', { count: 32, smart: true }),
    make('Uploaded Last 72 Hours', { count: 54, smart: true }),
    make('Operator', {
      children: [
        make('Daily Drilling Report (DDR)', { count: 85 }),
        make('Daily Geological Operations Summary', { count: 85 }),
        make('Composite Log (PDF)', { count: 21 }),
        make('Progress Chart (PDF)', { count: 79 }),
        make('Daily Lookahead', { count: 85 }),
        make('Daily Weather Forecast Report'),
        make('Daily Cost Report'),
        make('Mud Report'),
        make('SID', { count: 1 }),
      ],
    }),
    make('Mudlogging Report', {
      children: [
        make('Morning', {
          children: [make('Morning Report', { count: 76 }), make('Morning Timelog', { count: 75 })],
        }),
        make('Afternoon', {
          children: [
            make('Afternoon Report', { count: 72 }),
            make('Afternoon Timelog', { count: 68 }),
          ],
        }),
        make('Midnight', { children: [make('Midnight Report', { count: 74 })] }),
      ],
    }),
  ];
}

const DOC_NAMES = [
  'NAGA-2_TAPIS DEEP-1 RDR1_DDR 85_10.09.2026 Continue Wireline Run MDT 3. Perform Full BOP test',
  'NAGA-2_TAPIS DEEP-1 RDR1_DDR 84_09.09.2026 Wireline Run MDT 3 (Pretest, FLID, Sampling)',
  'NAGA-2_TAPIS DEEP-1 RDR1_DDR 83_08.09.2026 POOH BHA, Wireline Run 1, Wireline Run 2',
  'NAGA-2_TAPIS DEEP-1 RDR1_DDR 82_07.09.2026 Viscous Pill, Pump 19.4ppg RMP, POOH BHA',
];

/**
 * Documents uploaded during this mock session, newest first. There is no bucket
 * behind mock mode, so "uploading" is the two API calls and nothing else — but
 * the row has to show up in the list afterwards, or the flow cannot be seen.
 */
const UPLOADED = new Map<number, DocumentRow[]>();
let nextDocId = 9000;

function startUpload(folderId: number, body: { name?: string; contentType?: string }): Upload {
  const now = new Date().toISOString();
  const name = (body.name ?? 'untitled').trim();

  const document: DocumentRow = {
    id: (nextDocId += 1),
    wellId: 344,
    folderId,
    name,
    contentType: body.contentType || 'application/octet-stream',
    sizeBytes: 0,
    ext: name.includes('.') ? name.split('.').pop()!.toLowerCase() : '',
    status: 'pending',
    uploadedBy: 'mock',
    uploadedAt: null,
    createdAt: now,
    updatedAt: now,
  };

  UPLOADED.set(folderId, [document, ...(UPLOADED.get(folderId) ?? [])]);

  return {
    document,
    uploadUrl: 'mock://upload',
    expiresAt: new Date(Date.now() + 15 * 60 * 1000).toISOString(),
    contentType: document.contentType,
  };
}

function completeUpload(id: number, sizeBytes: number): DocumentRow | null {
  for (const rows of UPLOADED.values()) {
    const doc = rows.find((d) => d.id === id);
    if (!doc) continue;

    doc.sizeBytes = sizeBytes;
    doc.status = 'ready';
    doc.uploadedAt = new Date().toISOString();
    doc.updatedAt = doc.uploadedAt;
    return doc;
  }
  return null;
}

function documentsFor(folderId: number, page: number, perPage: number): DocumentPage {
  const mine = (UPLOADED.get(folderId) ?? []).filter((d) => d.status === 'ready');
  const total = 85 + mine.length;
  const start = (page - 1) * perPage;

  const items: DocumentRow[] = DOC_NAMES.map((name, i) => {
    const uploaded = new Date(Date.now() - (start + i) * 24 * 3600 * 1000);
    return {
      id: start + i + 1,
      wellId: 344,
      folderId,
      name,
      contentType: 'application/pdf',
      sizeBytes: (60 - i) * 1024,
      ext: 'pdf',
      status: 'ready',
      tags: TAGS.get(start + i + 1) ?? [],
      uploadedBy: 'mock',
      uploadedAt: uploaded.toISOString(),
      createdAt: uploaded.toISOString(),
      updatedAt: uploaded.toISOString(),
    };
  });

  for (const doc of mine) doc.tags = TAGS.get(doc.id) ?? [];

  return {
    items: page === 1 ? [...mine, ...items] : items,
    total,
    page,
    perPage,
  };
}

// ---- tags and knowledge: enough of each for the screens to be used ----

/** Tags by document id. The mock's documents share ids across folders, and so their tags. */
const TAGS = new Map<number, string[]>();
/** The rows that were tagged, as they looked then: what "by tag" lists. */
const TAGGED = new Map<number, DocumentRow>();

const STARTER_TAGS = ['Daily Report', 'Depth', 'Cost', 'NPT', 'Mud', 'Casing', 'Lithology', 'Gas', 'Pore Pressure'];

function wellTags(): WellTags {
  const counts = new Map<string, number>();
  for (const tags of TAGS.values()) for (const t of tags) counts.set(t, (counts.get(t) ?? 0) + 1);

  const items = [...counts].map(([tag, count]) => ({ tag, count })).sort((a, b) => b.count - a.count);
  const used = new Set(items.map((t) => t.tag.toLowerCase()));
  return { items, suggestions: STARTER_TAGS.filter((t) => !used.has(t.toLowerCase())) };
}

/** One list of articles per shelf: "general", "company-1", "well-344". */
const SHELVES = new Map<string, KbArticle[]>([
  [
    'general',
    [
      {
        id: 1,
        title: 'How to read a Daily Operation Report (DDR)',
        body: 'A Daily Operation Report (DDR) is issued once per day per well and numbered in sequence. DOL is days on location, DFS days from spud. NPT is non-productive time in hours.',
        embedded: true,
        createdAt: new Date(0).toISOString(),
        updatedAt: new Date(0).toISOString(),
      },
    ],
  ],
]);
let nextArticleId = 1;

function shelfKeyOf(path: string): { key: string; companyId: number | null; wellId: number | null } | null {
  if (path === '/kb/articles') return { key: 'general', companyId: null, wellId: null };

  const company = /^\/companies\/(\d+)\/knowledge$/.exec(path);
  if (company) return { key: `company-${company[1]}`, companyId: Number(company[1]), wellId: null };

  const well = /^\/wells\/(\d+)\/knowledge$/.exec(path);
  if (well) return { key: `well-${well[1]}`, companyId: null, wellId: Number(well[1]) };

  return null;
}

function findArticle(id: number): { shelf: KbArticle[]; article: KbArticle } | null {
  for (const shelf of SHELVES.values()) {
    const article = shelf.find((a) => a.id === id);
    if (article) return { shelf, article };
  }
  return null;
}

/** The knowledge routes; null when the path is not one of them. */
function knowledgeRequest(path: string, init: RequestInit, admin: boolean): MockResult | null {
  if (path === '/kb/categories') return { status: 200, body: { items: [], total: 0 } };
  if (path.startsWith('/kb/terms')) {
    return init.method && init.method !== 'GET'
      ? { status: 503, body: { error: 'the glossary is not part of the mock' } }
      : { status: 200, body: { items: [], total: 0 } };
  }

  const readOnly: MockResult = { status: 403, body: { error: 'this knowledge is not yours to change' } };
  const input = () => JSON.parse(String(init.body ?? '{}')) as { title?: string; body?: string };

  const shelf = shelfKeyOf(path);
  if (shelf) {
    const items = SHELVES.get(shelf.key) ?? [];

    if (init.method === 'POST') {
      if (!admin) return readOnly;

      const { title, body } = input();
      if (!title?.trim() || !body?.trim()) {
        return { status: 400, body: { error: 'an article needs a title and a body' } };
      }
      if (items.some((a) => a.title.toLowerCase() === title.trim().toLowerCase())) {
        return { status: 409, body: { error: 'name already used here' } };
      }

      nextArticleId += 1;
      const now = new Date().toISOString();
      const created: KbArticle = {
        id: nextArticleId,
        title: title.trim(),
        body: body.trim(),
        companyId: shelf.companyId,
        wellId: shelf.wellId,
        embedded: true,
        createdAt: now,
        updatedAt: now,
      };
      SHELVES.set(shelf.key, [...items, created]);
      return { status: 201, body: created };
    }

    return { status: 200, body: { items, total: items.length, editable: admin } };
  }

  const one = /^\/kb\/articles\/(\d+)$/.exec(path);
  if (one) {
    const found = findArticle(Number(one[1]));
    if (!found) return { status: 404, body: { error: 'article not found' } };

    if (init.method === 'DELETE') {
      if (!admin) return readOnly;
      found.shelf.splice(found.shelf.indexOf(found.article), 1);
      return { status: 204, body: undefined };
    }
    if (init.method === 'PUT') {
      if (!admin) return readOnly;
      const { title, body } = input();
      if (!title?.trim() || !body?.trim()) {
        return { status: 400, body: { error: 'an article needs a title and a body' } };
      }
      found.article.title = title.trim();
      found.article.body = body.trim();
      found.article.updatedAt = new Date().toISOString();
    }
    return { status: 200, body: found.article };
  }

  return null;
}

// Built once per well and then mutated, so a folder created in mock mode is
// still there after the client refetches — as it would be against the API.
const TREES = new Map<number, Folder[]>();
let nextFolderId = 1000;

function treeOf(wellId: number): Folder[] {
  let tree = TREES.get(wellId);
  if (!tree) {
    tree = folderTree(wellId);
    TREES.set(wellId, tree);
  }
  return tree;
}

function walk(nodes: Folder[], visit: (f: Folder, parent: Folder[] ) => void) {
  for (const node of nodes) {
    visit(node, nodes);
    if (node.children) walk(node.children, visit);
  }
}

function findFolder(wellId: number, id: number) {
  let found: { folder: Folder; siblings: Folder[] } | null = null;
  walk(treeOf(wellId), (f, siblings) => {
    if (f.id === id) found = { folder: f, siblings };
  });
  return found as { folder: Folder; siblings: Folder[] } | null;
}

// ---- audit log and branding: enough for their admin pages to be tried ----

/** What happened in this tab, newest last. Gone on reload, like the mock's sessions. */
const AUDIT: AuditEvent[] = [];

function audit(e: Omit<AuditEvent, 'id' | 'at'>) {
  AUDIT.push({ id: AUDIT.length + 1, at: new Date().toISOString(), ...e });
}

function auditPage(path: string): AuditPage {
  const q = new URL(path, 'http://mock').searchParams;
  const has = (v: string | undefined, want: string | null) => !want || (v ?? '').toLowerCase().includes(want.toLowerCase());
  const items = [...AUDIT]
    .reverse()
    .filter(
      (e) =>
        (!q.get('kind') || e.kind === q.get('kind')) &&
        (!q.get('level') || e.level === q.get('level')) &&
        has(e.email, q.get('email')) &&
        (has(e.path, q.get('q')) || has(e.message, q.get('q'))),
    );
  const page = Math.max(1, Number(q.get('page')) || 1);
  const perPage = Math.min(200, Number(q.get('perPage')) || 50);
  return {
    items: items.slice((page - 1) * perPage, page * perPage),
    total: items.length,
    page,
    perPage,
    kinds: ['login', 'login_failed', 'logout', 'request', 'server_error', 'client_error'],
    retentionDays: 0,
  };
}

const BRANDING_KEY = 'wxp.mock.branding';

function readBranding(): Branding {
  try {
    const raw = localStorage.getItem(BRANDING_KEY);
    if (raw) return JSON.parse(raw) as Branding;
  } catch {
    /* fall through to empty */
  }
  return { brandName: '', appName: '', logo: '', backdrop: '' };
}

/** Resolves a request against the in-memory data, mimicking network latency. */
export async function mockRequest(
  path: string,
  init: RequestInit,
  token: string | null,
): Promise<MockResult> {
  await new Promise((r) => setTimeout(r, 180));

  if (path === '/auth/login') {
    const { email, password } = JSON.parse(String(init.body ?? '{}')) as {
      email?: string;
      password?: string;
    };

    const who = email?.trim().toLowerCase();
    if ((who !== DEMO_EMAIL && who !== ADMIN_EMAIL) || password !== DEMO_PASSWORD) {
      audit({ kind: 'login_failed', level: 'warn', email: who, method: 'POST', path: '/api/auth/login', status: 401, message: 'unauthorized' });
      return { status: 401, body: { error: 'The user name or password is incorrect.' } };
    }

    const admin = who === ADMIN_EMAIL;
    const user = admin ? ADMIN_USER : DEMO_USER;
    audit({ kind: 'login', level: 'info', email: user.email, role: user.role, method: 'POST', path: '/api/auth/login', status: 200 });
    return {
      status: 200,
      body: {
        token: admin ? ADMIN_TOKEN : MOCK_TOKEN,
        expiresAt: Math.floor(Date.now() / 1000) + 8 * 3600,
        user: admin ? ADMIN_USER : DEMO_USER,
      } satisfies LoginResponse,
    };
  }

  // Branding is read before anyone signs in: the sign-in page shows it.
  if (path === '/branding' && (init.method ?? 'GET') === 'GET') {
    return { status: 200, body: readBranding() };
  }

  // Everything below needs a session.
  if (token !== MOCK_TOKEN && token !== ADMIN_TOKEN) {
    return { status: 401, body: { error: 'missing or invalid token' } };
  }

  if (path === '/auth/me') {
    return { status: 200, body: token === ADMIN_TOKEN ? ADMIN_USER : DEMO_USER };
  }
  const me = token === ADMIN_TOKEN ? ADMIN_USER : DEMO_USER;

  if (path === '/auth/logout') {
    audit({ kind: 'logout', level: 'info', email: me.email, role: me.role, method: 'POST', path: '/api/auth/logout', status: 204 });
    return { status: 204, body: undefined };
  }

  if (path === '/audit/client-errors') {
    const report = JSON.parse(String(init.body ?? '{}')) as { message?: string; stack?: string; url?: string; source?: string };
    audit({
      kind: 'client_error',
      level: 'error',
      email: me.email,
      role: me.role,
      path: report.url,
      message: report.message,
      detail: [report.source && `source: ${report.source}`, report.stack].filter(Boolean).join('\n'),
    });
    return { status: 204, body: undefined };
  }

  if (path.startsWith('/audit')) {
    if (token !== ADMIN_TOKEN) return { status: 403, body: { error: 'forbidden' } };
    return { status: 200, body: auditPage(path) };
  }

  if (path === '/branding' && init.method === 'PUT') {
    if (token !== ADMIN_TOKEN) return { status: 403, body: { error: 'forbidden' } };
    const next = {
      ...(JSON.parse(String(init.body ?? '{}')) as Branding),
      updatedAt: new Date().toISOString(),
      updatedBy: me.email,
    };
    try {
      localStorage.setItem(BRANDING_KEY, JSON.stringify(next));
    } catch {
      return { status: 503, body: { error: 'This browser will not store the branding.' } };
    }
    return { status: 200, body: next };
  }
  if (path === '/wells') return { status: 200, body: { items: WELLS, total: WELLS.length } };

  const dash = /^\/wells\/(\d+)\/dashboard$/.exec(path);
  if (dash) {
    const well = WELLS.find((w) => w.id === Number(dash[1]));
    if (!well) return { status: 404, body: { error: 'well not found' } };
    return { status: 200, body: dashboardFor(well) };
  }

  const folders = /^\/wells\/(\d+)\/folders$/.exec(path);
  if (folders) {
    const wellId = Number(folders[1]);

    if (init.method === 'POST') {
      const { name, parentId } = JSON.parse(String(init.body ?? '{}')) as {
        name?: string;
        parentId?: number | null;
      };
      if (!name?.trim()) {
        return { status: 400, body: { error: 'name must not be empty' } };
      }

      nextFolderId += 1;
      const created: Folder = {
        id: nextFolderId,
        wellId,
        parentId: parentId ?? null,
        name: name.trim(),
        position: 999,
        kind: 'folder',
        documentCount: 0,
      };

      if (parentId == null) {
        treeOf(wellId).push(created);
      } else {
        const target = findFolder(wellId, parentId);
        if (!target) return { status: 404, body: { error: 'parent folder not found' } };
        target.folder.children = [...(target.folder.children ?? []), created];
      }

      return { status: 201, body: created };
    }

    return { status: 200, body: { wellId, items: treeOf(wellId) } };
  }

  const folder = /^\/folders\/(\d+)$/.exec(path);
  if (folder) {
    const id = Number(folder[1]);
    const target = findFolder(344, id);
    if (!target) return { status: 404, body: { error: 'folder not found' } };

    if (init.method === 'DELETE') {
      target.siblings.splice(target.siblings.indexOf(target.folder), 1);
      return { status: 204, body: undefined };
    }

    const { name } = JSON.parse(String(init.body ?? '{}')) as { name?: string };
    if (name?.trim()) target.folder.name = name.trim();
    return { status: 200, body: target.folder };
  }

  const documents = /^\/folders\/(\d+)\/documents/.exec(path);
  if (documents) {
    if (init.method === 'POST') {
      const body = JSON.parse(String(init.body ?? '{}')) as { name?: string; contentType?: string };
      return { status: 201, body: startUpload(Number(documents[1]), body) };
    }

    const query = new URLSearchParams(path.split('?')[1] ?? '');
    return {
      status: 200,
      body: documentsFor(
        Number(documents[1]),
        Number(query.get('page')) || 1,
        Number(query.get('perPage')) || 20,
      ),
    };
  }

  const knowledge = knowledgeRequest(path.split('?')[0], init, token === ADMIN_TOKEN);
  if (knowledge) return knowledge;

  if (/^\/wells\/\d+\/document-tags$/.test(path)) return { status: 200, body: wellTags() };

  const byTag = /^\/wells\/\d+\/documents\?/.exec(path);
  if (byTag) {
    const wanted = (new URLSearchParams(path.split('?')[1]).get('tag') ?? '').toLowerCase();
    const items = [...TAGGED.values()]
      .map((d) => ({ ...d, tags: TAGS.get(d.id) ?? [] }))
      .filter((d) => d.tags.some((t) => t.toLowerCase() === wanted));
    return { status: 200, body: { items, total: items.length, page: 1, perPage: items.length || 1 } };
  }

  // Tagging: the one thing the mock lets a document be patched with.
  const patched = /^\/documents\/(\d+)$/.exec(path);
  if (patched && init.method === 'PATCH') {
    const id = Number(patched[1]);
    const { tags } = JSON.parse(String(init.body ?? '{}')) as { tags?: string[] };

    const seen = new Set<string>();
    const clean = (tags ?? [])
      .map((t) => t.trim().replace(/\s+/g, ' '))
      .filter((t) => t && !seen.has(t.toLowerCase()) && seen.add(t.toLowerCase()));

    const row =
      [...UPLOADED.values()].flat().find((d) => d.id === id) ??
      documentsFor(1, 1, 200).items.find((d) => d.id === id);
    if (!row) return { status: 404, body: { error: 'document not found' } };

    TAGS.set(id, clean);
    if (clean.length > 0) TAGGED.set(id, row);
    else TAGGED.delete(id);
    return { status: 200, body: { ...row, tags: clean } };
  }

  const complete = /^\/documents\/(\d+)\/complete$/.exec(path);
  if (complete) {
    const { sizeBytes } = JSON.parse(String(init.body ?? '{}')) as { sizeBytes?: number };
    const doc = completeUpload(Number(complete[1]), sizeBytes ?? 0);

    return doc
      ? { status: 200, body: doc }
      : { status: 404, body: { error: 'document not found' } };
  }

  return { status: 404, body: { error: `no mock handler for ${path}` } };
}
